import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Sun, Moon, Search } from 'lucide-react';
import { useTheme } from 'next-themes';
import { PerformanceButton } from '@/components/performance/PerformanceButton';
import { PresenceAvatars } from '@/components/layout/PresenceAvatars';

interface PageHeaderProps {
  title: string;
  description?: string;
  showSearch?: boolean;
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
  actions?: React.ReactNode;
  className?: string;
}

export function PageHeader({
  title,
  description,
  showSearch = false,
  searchQuery = '',
  onSearchChange,
  actions,
  className,
}: PageHeaderProps) {
  const { theme, setTheme } = useTheme();

  return (
    <div className={cn('mb-6 rounded-2xl border border-border bg-muted/50 px-5 py-4', className)}>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="space-y-0.5">
          <h1 className="text-foreground font-semibold text-xl uppercase tracking-tight">
            {title}
          </h1>
          {description && (
            <p className="text-slate-600 text-[10px] font-medium uppercase tracking-widest mt-0.5">
              {description}
            </p>
          )}
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <PresenceAvatars />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
            className="h-9 w-9 p-0 rounded-xl border border-border text-muted-foreground hover:text-emerald-400 hover:bg-muted/50 transition-colors"
            title="Toggle theme"
          >
            {theme === 'dark'
              ? <Sun className="h-4 w-4 text-amber-400" />
              : <Moon className="h-4 w-4" />
            }
          </Button>
          <PerformanceButton />
          {actions}
        </div>
      </div>

      <div className="mt-4 h-px bg-muted/50" />

      {showSearch && onSearchChange && (
        <div className="relative mt-4 w-full max-w-sm animate-fade-in">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-600" />
          <Input
            placeholder="Search…"
            value={searchQuery}
            onChange={e => onSearchChange(e.target.value)}
            className="pl-9 h-10 rounded-xl bg-black/40 border-border text-foreground text-[11px] placeholder:text-slate-700"
          />
        </div>
      )}
    </div>
  );
}
